import {
  createField,
  getComponentMaxWidthManifestFields,
  type FieldType,
} from '~/utils/manifest-utils.ts';
import {
  createFontFields,
  createMarginFields,
  createPaddingFields,
  createStyleField,
} from '~/utils/create-style-field.ts';

type ManifestStyleConditionalField = {
  readonly key: string;
  readonly condition_value: string | number | boolean;
};

type ManifestStyleLeafField = {
  readonly key: string;
  readonly label: string;
  readonly type: FieldType;
  readonly initial_value?: string | number | boolean | null;
  readonly options?: ReadonlyArray<{
    readonly text: string;
    readonly value: string;
  }>;
  readonly conditional_fields?: ReadonlyArray<ManifestStyleConditionalField>;
  readonly rules?: 'all_conditions';
};

type ManifestStyleGroupField = {
  readonly group: true;
  readonly label: string;
  readonly folded?: boolean;
  readonly fields: ReadonlyArray<ManifestStyleLeafField>;
};

type ManifestStyleField = ManifestStyleLeafField | ManifestStyleGroupField;

/**
 * Builds a typed manifest style group container.
 */
function createManifestStyleGroup({
  label,
  folded = false,
  fields,
}: {
  label: string;
  folded?: boolean;
  fields: ReadonlyArray<ManifestStyleLeafField>;
}): ManifestStyleGroupField {
  return {
    group: true,
    label,
    folded,
    fields,
  };
}

const textTransformOptions = [
  { text: 'Default', value: 'default' },
  { text: 'Lowercase', value: 'lowercase' },
  { text: 'Uppercase', value: 'uppercase' },
  { text: 'Capitalize', value: 'capitalize' },
] as const;

const alignmentOptions = [
  { text: 'Left', value: 'left' },
  { text: 'Center', value: 'center' },
  { text: 'Right', value: 'right' },
] as const;

const titleEnabledCondition = [
  { key: 'styles/title_switch', condition_value: true },
];

const titleBesideCondition = [
  { key: 'styles/title_switch', condition_value: true },
  { key: 'styles/title_position', condition_value: 'beside' },
];

const descriptionEnabledCondition = [
  { key: 'styles/description_switch', condition_value: true },
];

const errorEnabledCondition = [
  { key: 'styles/error_switch', condition_value: true },
];

const errorLeadingAssetEnabledCondition = [
  { key: 'styles/error_switch', condition_value: true },
  { key: 'styles/error_leading_asset_switch', condition_value: true },
];

const characterCountEnabledCondition = [
  { key: 'styles/character_count_switch', condition_value: true },
];

const requiredLabelEnabledCondition = [
  { key: 'styles/required_label_switch', condition_value: true },
];

const inputAllSidesBorderCondition = [
  { key: 'styles/input_border_style', condition_value: 'all_sides' },
];

const inputBottomBorderCondition = [
  { key: 'styles/input_border_style', condition_value: 'bottom' },
];

const showPasswordEnabledCondition = [
  { key: 'styles/show_password_button_switch', condition_value: true },
];

/**
 * Creates alignment, text transform, and number-of-lines manifest fields.
 */
function createTypographyExtraFields({
  key,
  conditional_fields,
  alignment = 'left',
  text_transform = 'default',
  number_of_lines = 1,
}: {
  key: string;
  conditional_fields?: ReadonlyArray<{
    readonly key: string;
    readonly condition_value: string | number | boolean;
  }>;
  alignment?: string;
  text_transform?: string;
  number_of_lines?: number;
}): ManifestStyleLeafField[] {
  return [
    createField({
      key: `${key}_alignment`,
      label: 'Alignment',
      type: 'select',
      initial_value: alignment,
      options: [...alignmentOptions],
      conditional_fields,
    }),
    createField({
      key: `${key}_text_transform`,
      label: 'Text transform',
      type: 'select',
      initial_value: text_transform,
      options: [...textTransformOptions],
      conditional_fields,
    }),
    createField({
      key: `${key}_number_of_lines`,
      label: 'Number of lines',
      type: 'number_input',
      initial_value: number_of_lines,
      conditional_fields,
    }),
  ];
}

/**
 * Creates default/hover/active/error color fields for a prefixed style group.
 */
function createStateColorFields({
  prefix,
  label,
  type,
  defaults,
  conditional_fields: conditionalFields,
}: {
  prefix: string;
  label: string;
  type: 'background_color' | 'border_color';
  defaults: {
    default: string;
    hover: string;
    active: string;
    error: string;
  };
  conditional_fields?: ReadonlyArray<{
    readonly key: string;
    readonly condition_value: string | number | boolean;
  }>;
}): ManifestStyleLeafField[] {
  const states = ['default', 'hover', 'active', 'error'] as const;

  return states.map((state) =>
    createField({
      key: `${prefix}_${state}_${type}`,
      label: `${state.charAt(0).toUpperCase()}${state.slice(1)} ${label} color`,
      type: 'color_picker_rgba',
      initial_value: defaults[state],
      conditional_fields: conditionalFields as Array<{
        key: string;
        condition_value: string | number | boolean;
      }>,
    })
  );
}

/**
 * Returns the Quick Brick text field plugin style manifest fields.
 */
export function getTextFieldManifestStyleFields(): ManifestStyleField[] {
  return [
    createManifestStyleGroup({
      label: 'Component max width',
      fields: [...getComponentMaxWidthManifestFields()],
    }),
    createManifestStyleGroup({
      label: 'Component',
      fields: [...getTextFieldComponentLayoutFields()],
    }),
    createManifestStyleGroup({
      label: 'Title',
      fields: [
        createField({
          key: 'title_switch',
          label: 'Enable',
          type: 'switch',
          initial_value: true,
        }),
        createField({
          key: 'title_position',
          label: 'Position',
          type: 'select',
          initial_value: 'above',
          options: [
            { text: 'Above', value: 'above' },
            { text: 'Beside', value: 'beside' },
          ],
          conditional_fields: titleEnabledCondition,
        }),
        createField({
          key: 'title_width',
          label: 'Title width',
          type: 'number_input',
          initial_value: 100,
          conditional_fields: titleBesideCondition,
        }),
        createField({
          key: 'title_vertical_alignment',
          label: 'Title vertical alignment',
          type: 'select',
          initial_value: 'top',
          options: [
            { text: 'Top', value: 'top' },
            { text: 'Center', value: 'center' },
          ],
          conditional_fields: titleBesideCondition,
        }),
        createStyleField({
          key: 'title',
          type: 'gutter',
          label: 'Gutter',
          initial_value: 8,
          conditional_fields: titleEnabledCondition,
        }),
        createStyleField({
          key: 'title',
          type: 'font_color',
          label: 'Color',
          initial_value: 'rgba(239, 239, 239, 1)',
          conditional_fields: titleEnabledCondition,
        }),
        ...createFontFields({
          key: 'title',
          label: 'Title',
          conditional_fields: titleEnabledCondition,
          initial_values: {
            font_family: 'Ubuntu-Medium',
            font_size: 16,
            line_height: 20,
            letter_spacing: 0,
          },
        }),
        ...createTypographyExtraFields({
          key: 'title',
          conditional_fields: titleEnabledCondition,
        }),
        ...createMarginFields({
          key: 'title',
          label: 'Title',
          initial_value: 0,
          conditional_fields: titleEnabledCondition,
        }),
      ],
    }),
    createManifestStyleGroup({
      label: 'Text Input',
      fields: [
        createStyleField({
          key: 'text_label',
          type: 'font_color',
          label: 'Placeholder label color',
          initial_value: 'rgba(158, 158, 158, 1)',
        }),
        createStyleField({
          key: 'text_label_active',
          type: 'font_color',
          label: 'Active label color',
          initial_value: 'rgba(239, 239, 239, 1)',
        }),
        createStyleField({
          key: 'text_label_error',
          type: 'font_color',
          label: 'Error label color',
          initial_value: 'rgba(239, 239, 239, 1)',
        }),
        ...createFontFields({
          key: 'text_label',
          label: 'Text label',
          initial_values: {
            font_family: 'Ubuntu-Regular',
            font_size: 16,
            line_height: 20,
            letter_spacing: 0,
          },
        }),
        ...createTypographyExtraFields({
          key: 'text_label',
          alignment: 'left',
        }),
        ...createMarginFields({
          key: 'text_label',
          label: 'Text label',
          initial_value: 0,
        }),
        createStyleField({
          key: 'input',
          type: 'corner_radius',
          label: 'Corner radius',
          initial_value: 16,
        }),
        createStyleField({
          key: 'input',
          type: 'gutter',
          label: 'Horizontal gutter',
          initial_value: 10,
        }),
        ...createPaddingFields({
          key: 'input',
          label: 'Input',
          initial_value: 16,
        }),
        ...createMarginFields({
          key: 'input',
          label: 'Input',
          initial_value: 0,
        }),
        ...createStateColorFields({
          prefix: 'input',
          label: 'background',
          type: 'background_color',
          defaults: {
            default: 'rgba(30, 30, 30, 1)',
            hover: 'rgba(30, 30, 30, 1)',
            active: 'rgba(46, 46, 46, 1)',
            error: 'rgba(76, 29, 39, 1)',
          },
        }),
        createField({
          key: 'input_border_style',
          label: 'Border style',
          type: 'select',
          initial_value: 'all_sides',
          options: [
            { text: 'All sides', value: 'all_sides' },
            { text: 'Bottom', value: 'bottom' },
          ],
        }),
        createField({
          key: 'input_border_position',
          label: 'Border position',
          type: 'select',
          initial_value: 'inside',
          options: [
            { text: 'Inside', value: 'inside' },
            { text: 'Center', value: 'center' },
            { text: 'Outside', value: 'outside' },
          ],
          conditional_fields: inputAllSidesBorderCondition,
        }),
        createStyleField({
          key: 'input_bottom_border',
          type: 'corner_radius',
          label: 'Bottom border corner radius',
          initial_value: 0,
          conditional_fields: inputBottomBorderCondition,
        }),
        createStyleField({
          key: 'input',
          type: 'border_thickness',
          label: 'Border size',
          initial_value: 1,
        }),
        ...createStateColorFields({
          prefix: 'input',
          label: 'border',
          type: 'border_color',
          defaults: {
            default: 'rgba(239, 239, 239, 0.25)',
            hover: 'rgba(239, 239, 239, 0.5)',
            active: 'rgba(239, 239, 239, 0.75)',
            error: 'rgba(254, 20, 72, 1)',
          },
        }),
      ],
    }),
    createManifestStyleGroup({
      label: 'Required label',
      folded: true,
      fields: [
        createField({
          key: 'required_label_switch',
          label: 'Enable',
          type: 'switch',
          initial_value: false,
        }),
        createField({
          key: 'required_label_position',
          label: 'Position',
          type: 'select',
          initial_value: 'below_input',
          options: [
            { text: 'Above input', value: 'above_input' },
            { text: 'Below input', value: 'below_input' },
          ],
          conditional_fields: requiredLabelEnabledCondition,
        }),
        createStyleField({
          key: 'required_label',
          type: 'font_color',
          label: 'Color',
          initial_value: 'rgba(239, 239, 239, 1)',
          conditional_fields: requiredLabelEnabledCondition,
        }),
        ...createFontFields({
          key: 'required_label',
          label: 'Required label',
          conditional_fields: requiredLabelEnabledCondition,
          initial_values: {
            font_family: 'Ubuntu-Medium',
            font_size: 12,
            line_height: 14,
            letter_spacing: 0,
          },
        }),
        createField({
          key: 'required_label_text_transform',
          label: 'Text transform',
          type: 'select',
          initial_value: 'default',
          options: [...textTransformOptions],
          conditional_fields: requiredLabelEnabledCondition,
        }),
        createField({
          key: 'required_label_alignment',
          label: 'Alignment',
          type: 'select',
          initial_value: 'left',
          options: [...alignmentOptions],
          conditional_fields: requiredLabelEnabledCondition,
        }),
        ...createMarginFields({
          key: 'required_label',
          label: 'Required label',
          initial_value: 0,
          conditional_fields: requiredLabelEnabledCondition,
        }),
      ],
    }),
    createManifestStyleGroup({
      label: 'Character Count',
      folded: true,
      fields: [
        createField({
          key: 'character_count_switch',
          label: 'Enable',
          type: 'switch',
          initial_value: false,
        }),
        createField({
          key: 'character_count_position',
          label: 'Position',
          type: 'select',
          initial_value: 'below_input',
          options: [
            { text: 'Text input', value: 'text_input' },
            { text: 'Above input', value: 'above_input' },
            { text: 'Below input', value: 'below_input' },
          ],
          conditional_fields: characterCountEnabledCondition,
        }),
        createStyleField({
          key: 'character_count',
          type: 'font_color',
          label: 'Default color',
          initial_value: 'rgba(158, 158, 158, 1)',
          conditional_fields: characterCountEnabledCondition,
        }),
        createStyleField({
          key: 'character_count_error',
          type: 'font_color',
          label: 'Error color',
          initial_value: 'rgba(254, 20, 72, 1)',
          conditional_fields: characterCountEnabledCondition,
        }),
        ...createFontFields({
          key: 'character_count',
          label: 'Character count',
          conditional_fields: characterCountEnabledCondition,
          initial_values: {
            font_family: 'Ubuntu-Medium',
            font_size: 12,
            line_height: 14,
            letter_spacing: 0,
          },
        }),
        createField({
          key: 'character_count_alignment',
          label: 'Alignment',
          type: 'select',
          initial_value: 'right',
          options: [...alignmentOptions],
          conditional_fields: characterCountEnabledCondition,
        }),
        ...createMarginFields({
          key: 'character_count',
          label: 'Character count',
          initial_value: 0,
          conditional_fields: characterCountEnabledCondition,
        }),
      ],
    }),
    createManifestStyleGroup({
      label: 'Description',
      folded: true,
      fields: [
        createField({
          key: 'description_switch',
          label: 'Enable',
          type: 'switch',
          initial_value: false,
        }),
        createStyleField({
          key: 'description',
          type: 'font_color',
          label: 'Color',
          initial_value: 'rgba(158, 158, 158, 1)',
          conditional_fields: descriptionEnabledCondition,
        }),
        ...createFontFields({
          key: 'description',
          label: 'Description',
          conditional_fields: descriptionEnabledCondition,
          initial_values: {
            font_family: 'Ubuntu-Regular',
            font_size: 13,
            line_height: 20,
            letter_spacing: 0,
          },
        }),
        ...createTypographyExtraFields({
          key: 'description',
          conditional_fields: descriptionEnabledCondition,
          number_of_lines: 3,
        }),
        ...createMarginFields({
          key: 'description',
          label: 'Description',
          initial_value: 0,
          conditional_fields: descriptionEnabledCondition,
        }),
      ],
    }),
    createManifestStyleGroup({
      label: 'Error',
      fields: [
        createField({
          key: 'error_switch',
          label: 'Enable',
          type: 'switch',
          initial_value: true,
        }),
        createStyleField({
          key: 'error',
          type: 'font_color',
          label: 'Color',
          initial_value: 'rgba(254, 20, 72, 1)',
          conditional_fields: errorEnabledCondition,
        }),
        ...createFontFields({
          key: 'error',
          label: 'Error',
          conditional_fields: errorEnabledCondition,
          initial_values: {
            font_family: 'Ubuntu-Medium',
            font_size: 13,
            line_height: 20,
            letter_spacing: 0,
          },
        }),
        ...createTypographyExtraFields({
          key: 'error',
          conditional_fields: errorEnabledCondition,
        }),
        ...createMarginFields({
          key: 'error',
          label: 'Error',
          initial_value: 0,
          conditional_fields: errorEnabledCondition,
        }),
        createField({
          key: 'error_container_display',
          label: 'Display',
          type: 'select',
          initial_value: 'fill',
          options: [
            { text: 'Fill', value: 'fill' },
            { text: 'Fit', value: 'fit' },
          ],
          conditional_fields: errorEnabledCondition,
        }),
        createStyleField({
          key: 'error_container',
          type: 'corner_radius',
          label: 'Corner radius',
          initial_value: 0,
          conditional_fields: errorEnabledCondition,
        }),
        createStyleField({
          key: 'error_container',
          type: 'gutter',
          label: 'Horizontal gutter',
          initial_value: 8,
          conditional_fields: errorEnabledCondition,
        }),
        ...createPaddingFields({
          key: 'error_container',
          label: 'Error container',
          initial_value: 0,
          conditional_fields: errorEnabledCondition,
        }),
        ...createMarginFields({
          key: 'error_container',
          label: 'Error container',
          initial_value: 0,
          conditional_fields: errorEnabledCondition,
        }),
        createStyleField({
          key: 'error',
          type: 'background_color',
          label: 'Background color',
          initial_value: 'transparent',
          conditional_fields: errorEnabledCondition,
        }),
        createStyleField({
          key: 'error',
          type: 'border_thickness',
          label: 'Border size',
          initial_value: 0,
          conditional_fields: errorEnabledCondition,
        }),
        createStyleField({
          key: 'error',
          type: 'border_color',
          label: 'Border color',
          initial_value: 'transparent',
          conditional_fields: errorEnabledCondition,
        }),
        createField({
          key: 'error_leading_asset_switch',
          label: 'Enable leading asset',
          type: 'switch',
          initial_value: false,
          conditional_fields: errorEnabledCondition,
        }),
        createField({
          key: 'error_leading_asset_vertical_alignment',
          label: 'Asset vertical alignment',
          type: 'select',
          initial_value: 'top',
          options: [
            { text: 'Top', value: 'top' },
            { text: 'Center', value: 'center' },
            { text: 'Bottom', value: 'bottom' },
          ],
          conditional_fields: errorLeadingAssetEnabledCondition,
        }),
        createStyleField({
          key: 'error_leading_asset',
          type: 'width',
          label: 'Asset width',
          initial_value: 20,
          conditional_fields: errorLeadingAssetEnabledCondition,
        }),
        createStyleField({
          key: 'error_leading_asset',
          type: 'height',
          label: 'Asset height',
          initial_value: 20,
          conditional_fields: errorLeadingAssetEnabledCondition,
        }),
        createField({
          key: 'error_leading_asset',
          label: 'Asset',
          type: 'uploader',
          conditional_fields: errorLeadingAssetEnabledCondition,
        }),
        ...createMarginFields({
          key: 'error_leading_asset',
          label: 'Error asset',
          initial_value: 0,
          conditional_fields: errorLeadingAssetEnabledCondition,
        }),
      ],
    }),
    createManifestStyleGroup({
      label: 'Show password button',
      folded: true,
      fields: [
        createField({
          key: 'show_password_button_switch',
          label: 'Enable',
          type: 'switch',
          initial_value: false,
        }),
        createStyleField({
          key: 'show_password_button',
          type: 'width',
          label: 'Width',
          initial_value: 20,
          conditional_fields: showPasswordEnabledCondition,
        }),
        createStyleField({
          key: 'show_password_button',
          type: 'height',
          label: 'Height',
          initial_value: 20,
          conditional_fields: showPasswordEnabledCondition,
        }),
        createField({
          key: 'show_password_button_default_hidden_asset',
          label: 'Default hidden asset',
          type: 'uploader',
          initial_value: null,
          conditional_fields: showPasswordEnabledCondition,
        }),
        createField({
          key: 'show_password_button_enable_hidden_hover_asset',
          label: 'Enable hidden hover asset',
          type: 'switch',
          initial_value: false,
          conditional_fields: showPasswordEnabledCondition,
        }),
        {
          ...createField({
            key: 'show_password_button_hover_hidden_asset',
            label: 'Hover hidden asset',
            type: 'uploader',
            initial_value: null,
            conditional_fields: [
              ...showPasswordEnabledCondition,
              {
                key: 'styles/show_password_button_enable_hidden_hover_asset',
                condition_value: true,
              },
            ],
          }),
          rules: 'all_conditions',
        },
        createField({
          key: 'show_password_button_default_visible_asset',
          label: 'Default visible asset',
          type: 'uploader',
          initial_value: null,
          conditional_fields: showPasswordEnabledCondition,
        }),
        createField({
          key: 'show_password_button_enable_visible_hover_asset',
          label: 'Enable visible hover asset',
          type: 'switch',
          initial_value: false,
          conditional_fields: showPasswordEnabledCondition,
        }),
        {
          ...createField({
            key: 'show_password_button_hover_visible_asset',
            label: 'Hover visible asset',
            type: 'uploader',
            initial_value: null,
            conditional_fields: [
              ...showPasswordEnabledCondition,
              {
                key: 'styles/show_password_button_enable_visible_hover_asset',
                condition_value: true,
              },
            ],
          }),
          rules: 'all_conditions',
        },
        createStyleField({
          key: 'show_password_button',
          type: 'margin_top',
          label: 'Margin top',
          initial_value: 0,
          conditional_fields: showPasswordEnabledCondition,
        }),
        createStyleField({
          key: 'show_password_button',
          type: 'margin_right',
          label: 'Margin right',
          initial_value: 0,
          conditional_fields: showPasswordEnabledCondition,
        }),
        createStyleField({
          key: 'show_password_button',
          type: 'margin_bottom',
          label: 'Margin bottom',
          initial_value: 0,
          conditional_fields: showPasswordEnabledCondition,
        }),
        createStyleField({
          key: 'show_password_button',
          type: 'margin_left',
          label: 'Margin left',
          initial_value: 0,
          conditional_fields: showPasswordEnabledCondition,
        }),
      ],
    }),
    createManifestStyleGroup({
      label: 'Date picker button',
      folded: true,
      fields: [
        createField({
          key: 'date_picker_button_default_asset',
          label: 'Default asset',
          type: 'uploader',
          initial_value: null,
        }),
        createField({
          key: 'date_picker_button_enable_focused_asset',
          label: 'Enable focused asset',
          type: 'switch',
          initial_value: false,
        }),
        {
          ...createField({
            key: 'date_picker_button_focused_asset',
            label: 'Focused asset',
            type: 'uploader',
            initial_value: null,
            conditional_fields: [
              {
                key: 'styles/date_picker_button_enable_focused_asset',
                condition_value: true,
              },
            ],
          }),
          rules: 'all_conditions',
        },
        createField({
          key: 'date_picker_button_enable_error_asset',
          label: 'Enable error asset',
          type: 'switch',
          initial_value: false,
        }),
        {
          ...createField({
            key: 'date_picker_button_error_asset',
            label: 'Error asset',
            type: 'uploader',
            initial_value: null,
            conditional_fields: [
              {
                key: 'styles/date_picker_button_enable_error_asset',
                condition_value: true,
              },
            ],
          }),
          rules: 'all_conditions',
        },
      ],
    }),
  ];
}

const TEXT_FIELD_BREAKPOINTS = [
  'mobile',
  'tablet',
  'desktop',
  'large_desktop',
] as const;

/**
 * Component layout fields for Zapp Studio using standard `component_{breakpoint}_*` keys.
 */
function getTextFieldComponentLayoutFields(): ManifestStyleLeafField[] {
  const fields: ManifestStyleLeafField[] = [];

  for (const breakpoint of TEXT_FIELD_BREAKPOINTS) {
    const labelPrefix =
      breakpoint === 'large_desktop'
        ? 'Large Desktop'
        : breakpoint[0].toUpperCase() + breakpoint.slice(1);

    fields.push(
      createField({
        key: `component_${breakpoint}_background_color`,
        type: 'color_picker_rgba',
        label: `Component ${labelPrefix} Background Color`,
        initial_value: 'rgba(0, 0, 0, 0)',
      })
    );
  }

  const spacingDefaults: Record<
    string,
    Partial<Record<(typeof TEXT_FIELD_BREAKPOINTS)[number], number | null>>
  > = {
    margin_top: { mobile: 0, tablet: 0, desktop: 0, large_desktop: 0 },
    margin_right: { mobile: 0, tablet: 0, desktop: 0, large_desktop: 0 },
    margin_bottom: {
      mobile: 40,
      tablet: 40,
      desktop: 48,
      large_desktop: 48,
    },
    margin_left: { mobile: 0, tablet: 0, desktop: 0, large_desktop: 0 },
    padding_top: { mobile: 0, tablet: 0, desktop: 0, large_desktop: 0 },
    padding_bottom: { mobile: 0, tablet: 0, desktop: 0, large_desktop: 0 },
    padding_left: {
      mobile: 24,
      tablet: 48,
      desktop: 64,
      large_desktop: 64,
    },
    padding_right: {
      mobile: 24,
      tablet: 48,
      desktop: 64,
      large_desktop: 64,
    },
  };

  for (const [sideKey, sideLabel] of [
    ['margin_top', 'Margin Top'],
    ['margin_right', 'Margin Right'],
    ['margin_bottom', 'Margin Bottom'],
    ['margin_left', 'Margin Left'],
    ['padding_top', 'Padding Top'],
    ['padding_right', 'Padding Right'],
    ['padding_bottom', 'Padding Bottom'],
    ['padding_left', 'Padding Left'],
  ] as const) {
    for (const breakpoint of TEXT_FIELD_BREAKPOINTS) {
      const labelPrefix =
        breakpoint === 'large_desktop'
          ? 'Large Desktop'
          : breakpoint[0].toUpperCase() + breakpoint.slice(1);

      fields.push(
        createField({
          key: `component_${breakpoint}_${sideKey}`,
          type: 'number_input',
          label: `Component ${labelPrefix} ${sideLabel}`,
          initial_value: spacingDefaults[sideKey]?.[breakpoint] ?? null,
        })
      );
    }
  }

  for (const breakpoint of TEXT_FIELD_BREAKPOINTS) {
    const labelPrefix =
      breakpoint === 'large_desktop'
        ? 'Large Desktop'
        : breakpoint[0].toUpperCase() + breakpoint.slice(1);

    fields.push(
      createField({
        key: `component_${breakpoint}_gutter_horizontal`,
        type: 'number_input',
        label: `Component ${labelPrefix} Horizontal Gutter`,
        initial_value: 10,
      })
    );
  }

  return fields;
}
